import { createServerFn } from '@tanstack/react-start'
import { z } from 'zod'
import { requireSupabaseAuth } from '@/integrations/supabase/auth-middleware'
import { requireAnyStaff, requireManager } from './role-guard'

/** The three crews the lodge schedules each day. */
export const STAFF_ROLES = [
  { id: 'hospitality', label: 'Hospitality Staff' },
  { id: 'dock', label: 'Dock Hands' },
  { id: 'processing', label: 'Fish Processing Staff' },
] as const

export type StaffRole = (typeof STAFF_ROLES)[number]['id']

export type StaffMember = {
  id: string
  name: string
  role: StaffRole
  phone: string | null
  email: string | null
  active: boolean
}

export type StaffShift = {
  id: string
  staff_id: string
  shift_date: string
  role: StaffRole
  note: string | null
}

type Ctx = { supabase: { from: (t: string) => any }; userId: string }

async function assertAdmin(context: unknown) {
  const { supabase, userId } = await requireManager(context)
  return { supabase, userId }
}

const roleSchema = z.enum(['hospitality', 'dock', 'processing'])
const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

/** Everyone on the crew list plus every shift inside the requested window. */
export const getStaffing = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ start: dateSchema, end: dateSchema }).parse(data),
  )
  .handler(async ({ data, context }) => {
    // Everyone on the crew can look at the staffing grid; only managers change it.
    const { supabase } = await requireAnyStaff(context)
    const [members, shifts] = await Promise.all([
      supabase
        .from('staff_members')
        .select('id,name,role,phone,email,active')
        .order('name', { ascending: true }),
      supabase
        .from('staff_shifts')
        .select('id,staff_id,shift_date,role,note')
        .gte('shift_date', data.start)
        .lte('shift_date', data.end)
        .order('shift_date', { ascending: true }),
    ])
    if (members.error) throw new Error(members.error.message)
    if (shifts.error) throw new Error(shifts.error.message)
    return {
      members: (members.data ?? []) as StaffMember[],
      shifts: (shifts.data ?? []) as StaffShift[],
    }
  })

export const saveStaffMember = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid().optional(),
        name: z.string().trim().min(1).max(120),
        role: roleSchema,
        phone: z.string().trim().max(40).nullable().optional(),
        email: z.string().trim().max(160).nullable().optional(),
        active: z.boolean().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { supabase } = await assertAdmin(context)
    const row = {
      name: data.name,
      role: data.role,
      phone: data.phone || null,
      email: data.email || null,
      active: data.active ?? true,
    }
    const query = data.id
      ? supabase.from('staff_members').update(row).eq('id', data.id)
      : supabase.from('staff_members').insert(row)
    const { data: saved, error } = await query
      .select('id,name,role,phone,email,active')
      .maybeSingle()
    if (error) throw new Error(error.message)
    return { ok: true, member: saved as StaffMember }
  })

export const removeStaffMember = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { supabase } = await assertAdmin(context)
    const { error } = await supabase.from('staff_members').delete().eq('id', data.id)
    if (error) throw new Error(error.message)
    return { ok: true }
  })

/** Put someone on, or take them off, one crew for one day. */
export const setStaffShift = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        staff_id: z.string().uuid(),
        shift_date: dateSchema,
        role: roleSchema,
        assigned: z.boolean(),
        note: z.string().trim().max(300).nullable().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { supabase } = await assertAdmin(context)
    if (!data.assigned) {
      const { error } = await supabase
        .from('staff_shifts')
        .delete()
        .eq('staff_id', data.staff_id)
        .eq('shift_date', data.shift_date)
        .eq('role', data.role)
      if (error) throw new Error(error.message)
      return { ok: true, shift: null }
    }
    const { data: saved, error } = await supabase
      .from('staff_shifts')
      .upsert(
        {
          staff_id: data.staff_id,
          shift_date: data.shift_date,
          role: data.role,
          note: data.note ?? null,
        },
        { onConflict: 'staff_id,shift_date,role' },
      )
      .select('id,staff_id,shift_date,role,note')
      .maybeSingle()
    if (error) throw new Error(error.message)
    return { ok: true, shift: saved as StaffShift }
  })
